import { computed, ref, Ref } from '@nuxtjs/composition-api';

export interface ActiveFilter {
  filterId: string;
  value: string;
}

export interface PriceRange {
  min: number;
  max: number;
}

interface UseProductListingParams {
  searchState: Ref<any>;
  sortingOptions: readonly any[];
  totalCount: Ref<number>;
  baseUrl?: string;
}

export const useProductListing = ({
  searchState,
  sortingOptions,
  totalCount,
  baseUrl = process.client ? window.location.href : ''
}: UseProductListingParams) => {
  const selectedSort = ref(sortingOptions[0]);

  const pageCurrent = computed(() => searchState.value.page + 1);
  const pagePrevious = computed(() => Math.max(1, pageCurrent.value - 1));
  const pageNext = computed(() => pageCurrent.value + 1);

  const isLoadingPreviousPage = ref(false);
  const isLoadingNextPage = ref(false);

  const updateSelectedSort = (sortOption: typeof sortingOptions[number]) => {
    if (sortOption === selectedSort.value) return;

    selectedSort.value = sortOption;
    searchState.value.indexName = sortOption.indexName;
    searchState.value.page = 0;
  };

  const groupByFilterId = (activeFilters: ActiveFilter[]) => {
    return activeFilters.reduce<Record<number, string[]>>((acc, cf) => {
      if (!acc[cf.filterId]) acc[cf.filterId] = [];
      acc[cf.filterId].push(cf.value);
      return acc;
    }, {});
  };

  const buildFacetFilters = (activeFilters: ActiveFilter[]) => {
    const grouped = groupByFilterId(activeFilters);

    return Object.entries(grouped).map(([filterId, values]) =>
      values.map(v => `${filterId}:${v}`)
    );
  };

  const updateFilters = ({
    activeFilters = [] as ActiveFilter[],
    priceRange = null
  }: {
    activeFilters?: ActiveFilter[];
    priceRange?: PriceRange | null;
  }) => {
    searchState.value.facetFilters = buildFacetFilters(activeFilters);

    searchState.value.numericFilters = priceRange
      ? [
        `priceAfterDiscount>=${priceRange.min}`,
        `priceAfterDiscount<=${priceRange.max}`
      ]
      : [];

    searchState.value.page = 0;
  };

  const waitUntilSearchFinished = () => {
    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (!searchState.value.isLoading) {
          clearInterval(interval);
          resolve(true);
        }
      }, 50);
    });
  };

  const loadNextPage = async () => {
    isLoadingNextPage.value = true;
    searchState.value.page += 1;
    await waitUntilSearchFinished();
    isLoadingNextPage.value = false;
  };

  const loadPreviousPage = async () => {
    if (searchState.value.page <= 0) return;
    isLoadingPreviousPage.value = true;
    searchState.value.page -= 1;
    await waitUntilSearchFinished();
    isLoadingPreviousPage.value = false;
  };

  const isMoreProducts = computed(
    () => (searchState.value.page + 1) * searchState.value.hitsPerPage < totalCount.value
  );

  const buildPageUrl = (pageNum: number) => {
    if (!baseUrl) return '';
    try {
      const url = new URL(baseUrl);
      url.searchParams.set('page', String(pageNum));
      return url.href;
    } catch {
      return '';
    }
  };

  const previousPageUrl = computed(() => buildPageUrl(pagePrevious.value));
  const nextPageUrl = computed(() => buildPageUrl(pageNext.value));
  const currentPageUrl = computed(() => buildPageUrl(pageCurrent.value));

  return {
    currentPageUrl,
    isLoadingNextPage,
    isLoadingPreviousPage,
    isMoreProducts,
    pageCurrent,
    pageNext,
    pagePrevious,
    previousPageUrl,
    selectedSort,
    nextPageUrl,
    updateFilters,
    loadNextPage,
    loadPreviousPage,
    updateSelectedSort
  };
};
